import { replay, type Turn } from "./rules.js";
import type { Game } from "./store.js";

/**
 * Views over finished games (PRD FR-5).
 *
 * Like everything else here, derived from the turn log rather than stored, so a
 * game's summary can never disagree with the turns it is built from.
 */

export interface GameSummary {
  game: Game;
  winnerId: string | null;
  winningScore: number;
  players: number;
  turns: number;
  /** Finished games are listed by when they ended, newest first. */
  at: number;
}

export function summariseGames(games: Game[]): GameSummary[] {
  return games
    .map((game) => {
      const winner = replay(game.order, game.turns).standings[0] ?? null;
      return {
        game,
        winnerId: winner?.playerId ?? null,
        winningScore: winner?.total ?? 0,
        players: game.order.length,
        turns: game.turns.length,
        at: game.finishedAt ?? game.startedAt,
      };
    })
    .sort((a, b) => b.at - a.at);
}

export interface RoundEntry {
  playerId: string;
  turn: Turn | null;
  /** Running total after this turn, so the table reads like the evening did. */
  total: number;
}

export interface Round {
  number: number;
  entries: RoundEntry[];
}

/**
 * FR-5.2: every turn, grouped implicitly by round.
 *
 * Rounds are the turn list chunked by the number of players, so the last one
 * can be short -- the game ends mid-round whenever the target is reached, and
 * padding it with blanks would invent turns nobody took.
 */
export function rounds(game: Game): Round[] {
  const n = game.order.length;
  if (n === 0) return [];

  const totals = new Map<string, number>(game.order.map((id) => [id, 0]));
  const out: Round[] = [];

  game.turns.forEach((turn, index) => {
    const roundNumber = Math.floor(index / n) + 1;
    let round = out[roundNumber - 1];
    if (!round) {
      round = { number: roundNumber, entries: [] };
      out[roundNumber - 1] = round;
    }
    // Replay rather than trusting a stored total: this is the same arithmetic
    // the scoreboard used, including the strike penalty.
    const upTo = replay(game.order, game.turns.slice(0, index + 1));
    const player = upTo.players.find((p) => p.playerId === turn.playerId);
    totals.set(turn.playerId, player?.total ?? 0);
    round.entries.push({
      playerId: turn.playerId,
      turn,
      total: player?.total ?? 0,
    });
  });

  return out;
}
